import {
	Grid,
	Heading,
	isRequired,
	moneyFormatNoDecimal,
	revertFormatNoDecimal,
	SimpleGrid,
	Stack,
	TextField,
	useText,
	validateMax,
	validateMin,
	validateStep,
} from "@sveabank/cc-common";
import { useEffect } from "react";
import { useForm } from "react-final-form";
import BorderDivider from "../../../components/border-divider";
import type { SelectedCreditProduct } from "../../../types/product";
import type { CreditScoreStepState } from "../../../types/step";
import { getCreditScoreMaxValue, getCreditScoreMinValue } from "../helpers";
import CreditConfigRemove from "./credit-config-remove-product";
import CreditConfigSlider from "./credit-config-slider";
import CreditProductInfo from "./credit-product-info";

type Props = {
	products: SelectedCreditProduct[];
	creditScore: CreditScoreStepState;
	title: string;
};

const CreditProductBlock = ({ products, creditScore, title }: Props) => {
	const [t, texts] = useText();
	const form = useForm();

	useEffect(() => {
		for (const product of products) {
			form.blur(product.productId);
		}
	}, []);

	return (
		<Stack gap={4}>
			<Heading.H3>{title}</Heading.H3>
			{products.map((product) => (
				<BorderDivider excludeFirst={false} key={product.productId}>
					<Stack gap={8}>
						<Grid>
							<Grid.Col span={{ base: 8, tablet: 8 }}>
								<CreditProductInfo product={product} />
							</Grid.Col>
							<Grid.Col span={{ base: 4, tablet: 4 }}>
								<CreditConfigRemove
									name={product.productId}
									sku={product.sku}
									isAddon={product.isAddon}
								/>
							</Grid.Col>
						</Grid>
						<SimpleGrid cols={2} gap={4}>
							<div className="tablet:order-1 order-2">
								<CreditConfigSlider
									name={product.productId}
									min={getCreditScoreMinValue(product, creditScore)}
									max={getCreditScoreMaxValue(product, creditScore)}
								/>
							</div>
							<div className="tablet:order-2 order-1">
								<TextField
									label={t(texts.adjustCredit.amount)}
									type="tel"
									aria-label="Välj kreditnivå"
									format={(value) => moneyFormatNoDecimal(+value)}
									parse={(value) => revertFormatNoDecimal(value)}
									name={product.productId}
									validators={[
										isRequired,
										validateMin(
											getCreditScoreMinValue(product, creditScore),
											t(
												texts.validation.minCredit,
												moneyFormatNoDecimal(getCreditScoreMinValue(product, creditScore), true),
											),
										),
										validateMax(
											getCreditScoreMaxValue(product, creditScore),
											t(
												texts.validation.maxCredit,
												moneyFormatNoDecimal(getCreditScoreMaxValue(product, creditScore), true),
											),
										),
										validateStep(1000),
									]}
									helperText={
										product.isAddon ? t(texts.adjustCredit.isAddon) : t(texts.adjustCredit.notAddon)
									}
								/>
							</div>
						</SimpleGrid>
					</Stack>
				</BorderDivider>
			))}
		</Stack>
	);
};

export default CreditProductBlock;
